import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAccountVerified, defaultPfpUrl } from "../utils";
import { createPost } from "../../api/api";
import { Button } from "../ui/button";
import { MentionTextarea } from "../ui/mention-textarea";
import { createMentionFetcher } from "../../lib/mentions";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "../../lib/utils";
import LocationSearch from "../../widgets/LocationSearch";
import SignedOutPrompt from "../SignedOutPrompt";
import ImagePreviewStrip from "./ImagePreviewStrip";
import {
  MAX_CONTENT_IMAGES,
  appendImageFiles,
  collectImageFiles,
  isFileDragEvent,
  uploadImageFile,
} from "../../lib/contentImages";

const POST_TYPES = [
  { value: "post", label: "Post" },
  { value: "prayer", label: "Prayer" },
  { value: "scripture", label: "Scripture" },
  { value: "sermon", label: "Sermon" },
];

const EMPTY_LOCATION = { value: "", lat: null, lon: null };

/**
 * True when an entity has a geocoded address that can pin a post.
 * @param {{ address?: string, latitude?: number, longitude?: number } | null | undefined} entity
 * @returns {boolean}
 */
function hasGeocodedAddress(entity) {
  const address = entity?.address?.trim?.() || "";
  const lat = Number(entity?.latitude);
  const lon = Number(entity?.longitude);
  return Boolean(address) && Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
}

/**
 * Default compose location: the org's own address, or the first affiliated
 * org (existing list order) that has a geocoded address.
 * @param {object | null | undefined} user
 * @returns {{ value: string, lat: number, lon: number } | null}
 */
function resolveDefaultPostLocation(user) {
  if (!user) return null;

  if (user.type === "organization") {
    if (!hasGeocodedAddress(user)) return null;
    return {
      value: user.address.trim(),
      lat: Number(user.latitude),
      lon: Number(user.longitude),
    };
  }

  const affiliations = Array.isArray(user.affiliations) ? user.affiliations : [];
  for (const affiliation of affiliations) {
    if (affiliation?.status && affiliation.status !== "active") continue;
    const org = affiliation?.organization;
    if (!hasGeocodedAddress(org)) continue;
    return {
      value: org.address.trim(),
      lat: Number(org.latitude),
      lon: Number(org.longitude),
    };
  }
  return null;
}

/**
 * @param {{ value?: string, lat?: number | null, lon?: number | null } | null} a
 * @param {{ value?: string, lat?: number | null, lon?: number | null } | null} b
 * @returns {boolean}
 */
function isSameLocation(a, b) {
  return Boolean(a && b) && a.value === b.value && a.lat === b.lat && a.lon === b.lon;
}

/**
 * Small icon button for the composer toolbar.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function IconButton({ onClick, label, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ui-border bg-ui-card text-text-primary transition hover:bg-highlight focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/**
 * Create a post — page, embedded, or modal composer variants.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} [props.user]
 * @param {"page"|"embedded"|"composer"} [props.variant]
 * @param {() => void} [props.onClose]
 * @param {(post?: object) => void} [props.onPostCreated]
 * @returns {JSX.Element}
 */
const CreatePost = ({ userId, user, variant = "page", onClose, onPostCreated }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const mentionRef = useRef(null);
  const locationInputRef = useRef(null);
  const didInitLocation = useRef(Boolean(user));
  const defaultLocation = useMemo(() => resolveDefaultPostLocation(user), [user]);
  const hasDefaultLocation = Boolean(defaultLocation);
  const [imageItems, setImageItems] = useState([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [locationData, setLocationData] = useState(() => defaultLocation || EMPTY_LOCATION);
  const [isDefaultLocation, setIsDefaultLocation] = useState(() => Boolean(defaultLocation));
  const [content, setContent] = useState("");
  const [postType, setPostType] = useState("post");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const imageItemsRef = useRef(imageItems);
  imageItemsRef.current = imageItems;

  const displayName = useMemo(() => {
    if (user?.name) return user.name;
    if (user?.first_name || user?.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user?.username || "User";
  }, [user]);

  const isComposer = variant === "composer" || variant === "embedded";
  const fetchCandidates = useMemo(
    () => createMentionFetcher(userId),
    [userId]
  );

  /**
   * Append image files from the picker or a drop.
   * @param {File[]|FileList} files
   * @returns {void}
   */
  function addImageFiles(files) {
    const selected = collectImageFiles(files);
    if (!selected.length) {
      if (files && (files.length || files.files?.length)) {
        setError("Please select an image file");
      }
      return;
    }
    const { items, error: addError } = appendImageFiles(
      selected,
      imageItemsRef.current
    );
    imageItemsRef.current = items;
    setImageItems(items);
    setError(addError);
  }

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;
    addImageFiles(selected);
  };

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragEnter(event) {
    if (submitting || !isFileDragEvent(event)) return;
    event.preventDefault();
    setIsDraggingFiles(true);
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragLeave(event) {
    if (!isFileDragEvent(event)) return;
    const related = event.relatedTarget;
    if (related && event.currentTarget.contains(related)) return;
    setIsDraggingFiles(false);
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDragOver(event) {
    if (submitting || !isFileDragEvent(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  /**
   * @param {DragEvent} event
   * @returns {void}
   */
  function handleFileDrop(event) {
    if (!isFileDragEvent(event)) return;
    event.preventDefault();
    setIsDraggingFiles(false);
    if (submitting) return;
    const files = collectImageFiles(event.dataTransfer);
    addImageFiles(files.length ? files : event.dataTransfer?.files);
  }

  const removeImage = (id) => {
    setImageItems((prev) => {
      const found = prev.find((item) => item.id === id);
      if (found) URL.revokeObjectURL(found.preview);
      const next = prev.filter((item) => item.id !== id);
      imageItemsRef.current = next;
      return next;
    });
  };

  const reorderImages = (fromId, toId) => {
    setImageItems((prev) => {
      const from = prev.findIndex((item) => item.id === fromId);
      const to = prev.findIndex((item) => item.id === toId);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      imageItemsRef.current = next;
      return next;
    });
  };

  const applyDefaultLocation = (nextDefault) => {
    if (nextDefault) {
      setLocationData(nextDefault);
      setIsDefaultLocation(true);
    } else {
      setLocationData(EMPTY_LOCATION);
      setIsDefaultLocation(false);
    }
  };

  const handleLocationChange = (next) => {
    setLocationData(next);
    setIsDefaultLocation(Boolean(defaultLocation) && isSameLocation(next, defaultLocation));
  };

  useEffect(() => {
    if (didInitLocation.current || !user) return;
    didInitLocation.current = true;
    applyDefaultLocation(defaultLocation);
  }, [user, defaultLocation]);

  useEffect(() => {
    if (isComposer) return;
    const frame = window.requestAnimationFrame(() => {
      if (hasDefaultLocation) {
        mentionRef.current?.focus({ preventScroll: true });
      } else {
        locationInputRef.current?.focus({ preventScroll: true });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hasDefaultLocation, isComposer]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!content.trim() && !imageItems.length) return;
    if (!userId) {
      setError("You must be logged in to post.");
      return;
    }
    if (user && !isAccountVerified(user)) {
      setError("Your account must be verified before you can create posts.");
      return;
    }

    const hasGeocodedLocation =
      Boolean(locationData.value?.trim()) &&
      Number.isFinite(locationData.lat) &&
      Number.isFinite(locationData.lon);

    setSubmitting(true);
    setError("");

    try {
      const images = [];
      for (const item of imageItems) {
        images.push(await uploadImageFile(item.file));
      }

      const caption = (mentionRef.current?.getSerializedValue?.() ?? content).trim();
      const result = await createPost({
        author_id: userId,
        author_is_org: user?.type === "organization",
        caption,
        images,
        location: hasGeocodedLocation ? locationData.value.trim() : "",
        latitude: hasGeocodedLocation ? locationData.lat : 0,
        longitude: hasGeocodedLocation ? locationData.lon : 0,
        post_type: postType,
      });

      if (result.error || result.success === false) {
        setError(result.error || result.message || "Failed to create post");
      } else {
        onPostCreated?.(result.data);
        onClose?.();
        if (variant === "page") {
          navigate("/feed");
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formBody = (
    <form
      onSubmit={handleSubmit}
      onDragEnter={handleFileDragEnter}
      onDragLeave={handleFileDragLeave}
      onDragOver={handleFileDragOver}
      onDrop={handleFileDrop}
      className={cn(
        "relative w-full",
        isComposer ? "bg-ui-card" : "max-w-2xl rounded-lg border border-ui-border bg-ui-card p-6 shadow-shell-sm"
      )}
    >
      {isDraggingFiles ? (
        <div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] border-2 border-dashed border-accent bg-ui-overlay"
          aria-hidden
        >
          <span className="rounded-md bg-ui-card px-3 py-2 text-sm text-text-primary shadow-sm">
            Drop photos to attach
          </span>
        </div>
      ) : null}
      {variant === "composer" ? (
        <div className="flex items-start justify-between gap-4 px-6 py-5">
          <div className="flex min-w-0 items-start gap-3">
            <img
              src={user?.pfp_url || defaultPfpUrl}
              alt=""
              className="h-10 w-10 rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.src = defaultPfpUrl;
              }}
            />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-text-primary">{displayName}</div>
              <div className="text-xs text-text-secondary">Post to Anyone</div>
            </div>
          </div>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ui-border text-text-primary hover:bg-highlight"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}

      {/* <div className={cn(isComposer ? "px-6 pb-3" : "mb-4")}>
        <Tabs value={postType} onValueChange={setPostType}>
          <TabsList className="w-full">
            {POST_TYPES.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div> */}

      <div className={cn(isComposer ? "px-6 pb-4" : "mb-4")}>
        <label className="mb-2 block text-sm font-semibold text-text-secondary" htmlFor="create-post-location">
          Location <span className="font-normal text-text-muted">(optional)</span>
        </label>
        <LocationSearch
          id="create-post-location"
          value={locationData}
          onChange={handleLocationChange}
          placeholder="Add an address so others can find this on the map"
          searchType="address"
          inputClassName="!bg-ui-background border-ui-border"
          inputRef={locationInputRef}
          selectTextOnClick={isDefaultLocation}
          autoFocus={!isComposer && !hasDefaultLocation}
          dataComposerAutofocus={!hasDefaultLocation}
        />
        <p className="mt-1.5 text-xs text-text-muted">
          Select a valid address from the suggestions to pin your post on Explore.
        </p>
      </div>

      <div className={cn(isComposer ? "px-6 pb-4" : "mb-4")}>
        {variant !== "composer" ? (
          <label className="mb-2 block text-sm font-semibold text-text-secondary" htmlFor="create-post-caption">
            Caption
          </label>
        ) : (
          <label className="sr-only" htmlFor="create-post-caption">
            Share something
          </label>
        )}
        <MentionTextarea
          id="create-post-caption"
          autoFocus={!isComposer && hasDefaultLocation}
          data-composer-autofocus={hasDefaultLocation ? "" : undefined}
          expanding={false}
          rows={isComposer ? 5 : 4}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          fetchCandidates={fetchCandidates}
          excludeId={userId}
          ref={mentionRef}
          className="resize-none bg-ui-background p-3 text-base"
          placeholder={
            postType === "prayer"
              ? "Share a prayer request…"
              : postType === "scripture"
                ? "Share a verse or reflection…"
                : postType === "sermon"
                  ? "Share a sermon note…"
                  : "What would you like to share?"
          }
        />
      </div>

      {imageItems.length ? (
        <div className={cn(isComposer ? "px-6 pb-4" : "mb-4")}>
          <ImagePreviewStrip
            items={imageItems}
            onReorder={reorderImages}
            onRemove={removeImage}
            onAdd={() => fileInputRef.current?.click()}
            disabled={submitting}
          />
        </div>
      ) : null}

      {error ? (
        <p className={cn("text-sm text-error", isComposer ? "px-6 pb-2" : "mb-3")}>{error}</p>
      ) : null}

      <div
        className={cn(
          "flex items-center justify-between gap-3",
          isComposer ? "border-t border-ui-border px-6 py-4" : "mt-2"
        )}
      >
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <IconButton
            label="Add photo"
            disabled={submitting || imageItems.length >= MAX_CONTENT_IMAGES}
            onClick={() => fileInputRef.current?.click()}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
              <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
              <path strokeWidth="2" d="M21 15l-5-5L5 21" />
            </svg>
          </IconButton>
        </div>
        <div className="flex items-center gap-2">
          {onClose && variant !== "composer" ? (
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          ) : null}
          <Button
            type="submit"
            disabled={submitting || (!content.trim() && !imageItems.length)}
          >
            {submitting ? "Posting…" : "Post"}
          </Button>
        </div>
      </div>
    </form>
  );

  if (!userId) {
    if (isComposer) {
      return null;
    }
    return (
      <SignedOutPrompt
        title="Create a post"
        description="Sign in to share posts, prayers, scripture, and sermons with your network."
      />
    );
  }

  if (user && !isAccountVerified(user)) {
    if (isComposer) {
      return null;
    }
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Create a post"
        description="Your account must be verified before you can create posts. Until then, you can browse Explore and manage your profile."
      />
    );
  }

  if (isComposer) {
    return <div className="w-full">{formBody}</div>;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-start bg-ui-background px-4 py-12">
      <header className="mb-8 w-full max-w-2xl text-center">
        <h1 className="font-display text-3xl font-medium text-text-primary">Create a New Post</h1>
        <p className="mt-2 text-text-secondary">Share with your network.</p>
      </header>
      {formBody}
    </div>
  );
};

export default CreatePost;
